import type { AtcRow, UpdateAtcInput } from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';
import { BusyButton, Field } from '../components/Field.js';

/**
 * 基线五项的录入表单（C-164 A2）。
 *
 * ## 为什么这一层非接不可
 *
 * 库与契约都齐了：五列在 `atc` 上、`updateAtcSchema` 收它们、`store.ts` 会写。
 * 界面里 `updateAtc` 只被用来改状态与改名——**于是这五项只能靠调接口填**，
 * 而那条路等于「我方替客户填业务事实」。产品提供位置，不提供内容。
 *
 * ## 两个耗时不是一个数
 *
 * 这里录的是**基线单次耗时**：改造前，人做一次要多久。
 * 它是改造成效的分母，随卡终身。改造后实际跑多久由执行记录自己算，不在这儿填。
 *
 * ## 频次的数与单位同生共死
 *
 * 只有数没有单位的「3」无法归一，落进视图会变成一个默认按天算的错值。
 * 库级有成对 CHECK，界面在提交前先拦一次——**同一条判据两处都写**，
 * 因为库那句话报出来是英文约束名，而人需要的是「填了数就得选单位」。
 *
 * ## 修订理由：首次可空，改动必填
 *
 * 基线是分母。**悄悄改分母等于改结论**——所以改的时候要留下为什么改。
 * 首次录入没有「前一个值」可解释，故那时可空。
 */
export function AtcBaselineForm({
  code,
  onSaved,
}: {
  /** 卡编码。用它读回这张卡当前的基线值。 */
  code: string;
  onSaved: () => void;
}): ReactNode {
  const [row, setRow] = useState<AtcRow | null>(null);
  const [duration, setDuration] = useState('');
  const [frequency, setFrequency] = useState('');
  const [unit, setUnit] = useState<'' | 'day' | 'week' | 'month'>('');
  const [estimated, setEstimated] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void api.atcs().then(
      (data) => {
        if (!live) return;
        const found = data.rows.find((r) => r.code === code) ?? null;
        setRow(found);
        if (found !== null) {
          setDuration(found.baselineDurationMin === null ? '' : String(found.baselineDurationMin));
          setFrequency(found.baselineFrequency === null ? '' : String(found.baselineFrequency));
          setUnit(found.baselineFrequencyUnit ?? '');
          setEstimated(found.baselineFrequencyEstimated);
          // 修订理由**不回填**：它说的是「这一次为什么改」，不是一个长期属性
          setReason('');
        }
      },
      (cause: unknown) => live && setError(messageOf(cause)),
    );
    return () => {
      live = false;
    };
  }, [code]);

  /** 这张卡已经有基线了吗。有，则这次是**修订**，理由必填。 */
  const hasBaseline = row !== null && row.baselineDurationMin !== null;

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSaved(false);

    const hasFrequency = frequency.trim() !== '';
    const hasUnit = unit !== '';
    if (hasFrequency !== hasUnit) {
      setError('频次的数与周期要一起填：填了「多少次」就得选「每天 / 每周 / 每月」');
      return;
    }
    if (hasBaseline && reason.trim() === '') {
      setError('这张卡已经有基线了。改它要写一句为什么改——基线是改造成效的分母。');
      return;
    }

    const body: UpdateAtcInput = {
      baselineDurationMin: duration.trim() === '' ? null : Number(duration),
      baselineFrequency: hasFrequency ? Number(frequency) : null,
      baselineFrequencyUnit: hasUnit ? unit : null,
      baselineFrequencyEstimated: estimated,
      baselineRevisionReason: reason.trim() === '' ? null : reason.trim(),
    };

    setBusy(true);
    try {
      const updated = await api.updateAtc(row!.id, body);
      setRow(updated);
      setSaved(true);
      onSaved();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  if (error !== null && row === null) return <p className="msg">{error}</p>;
  if (row === null) return <p className="sub-hint">读取中……</p>;

  return (
    <form onSubmit={(event) => void submit(event)}>
      <Field
        id="atc-baseline-duration"
        label="基线单次耗时"
        aux="改造前，人做一次要多少分钟"
      >
        <input
          id="atc-baseline-duration"
          type="number"
          min={1}
          max={100000}
          step={1}
          inputMode="numeric"
          placeholder="分钟"
          value={duration}
          onChange={(event) => setDuration(event.target.value)}
        />
      </Field>

      <Field id="atc-baseline-frequency" label="基线频次" aux="多少次，与右边的周期一起填">
        <input
          id="atc-baseline-frequency"
          type="number"
          min={0}
          step="any"
          inputMode="decimal"
          placeholder="次数"
          value={frequency}
          onChange={(event) => setFrequency(event.target.value)}
        />
      </Field>

      <Field id="atc-baseline-unit" label="频次周期">
        <select
          id="atc-baseline-unit"
          value={unit}
          onChange={(event) => setUnit(event.target.value as '' | 'day' | 'week' | 'month')}
        >
          <option value="">（未选）</option>
          <option value="day">每天</option>
          <option value="week">每周</option>
          <option value="month">每月</option>
        </select>
      </Field>

      <Field
        id="atc-baseline-estimated"
        label="频次是调研估计值"
        aux="事件触发的卡只能估。估的和实测的长得一样，不标就分不出来"
      >
        <input
          id="atc-baseline-estimated"
          type="checkbox"
          checked={estimated}
          onChange={(event) => setEstimated(event.target.checked)}
        />
      </Field>

      <Field
        id="atc-baseline-reason"
        label="修订理由"
        aux={hasBaseline ? '这张卡已有基线，改它必须写' : '首次录入可留空'}
      >
        <textarea
          id="atc-baseline-reason"
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>

      {row.baselineDailyMinutes !== null && (
        <p className="aux">
          按当前值算，这张卡一天占 <b>{row.baselineDailyMinutes}</b> 分钟。
          这个数由库算，不在界面上乘。
        </p>
      )}

      {error !== null && <p className="msg">{error}</p>}
      {saved && <p className="aux">已保存。</p>}

      <div className="row-actions">
        <BusyButton busy={busy} busyLabel="保存中" type="submit">
          保存基线
        </BusyButton>
      </div>
    </form>
  );
}
